/**
 * 권한 관리 시스템
 * userType별 메뉴 접근 권한을 중앙에서 관리합니다.
 *
 * 권한 레벨:
 * - 'full': 전체 접근 (CRUD 가능)
 * - 'readonly': 읽기 전용 (조회만 가능)
 * - 'hidden': 메뉴 숨김 (접근 불가)
 */

// 라우트별 권한 설정
const permissions = {
    // 프로필

    // 기초 정보
    '/partner/operation/company': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/members': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/operation/warehouse': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/operation/custom-equipment-model': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/operation/consumables': { owner: 'full', manager: 'full', member: 'readonly' },
    '/partner/customer': { owner: 'full', manager: 'full', member: 'readonly' },

    // 자산
    '/partner/resource/equipment': { owner: 'full', manager: 'full', member: 'readonly' },
    '/partner/resource/consumables': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/resource/consumable-inventory': { owner: 'full', manager: 'full', member: 'readonly' },

    // 수집 장비
    '/partner/printer': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/printer/unregistered': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/printer/agents': { owner: 'full', manager: 'full', member: 'full' },

    // 모니터링
    '/partner/monitoring/data/usage': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/monitoring/data/supplies': { owner: 'full', manager: 'full', member: 'readonly' },
    '/partner/monitoring/customer': { owner: 'full', manager: 'full', member: 'full' },

    // A/S
    '/partner/as/register': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/as/progress': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/as/completed': { owner: 'full', manager: 'full', member: 'full' },
    '/partner/as/consumable-usages': { owner: 'full', manager: 'full', member: 'full' },

    // 계약
    '/partner/contract/uncontracted-equipment': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/contract/list': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/contract/invoice': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/contract/invoice/issued': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/contract/sales': { owner: 'full', manager: 'full', member: 'hidden' },

    // 업무관리
    '/partner/penalty/billing': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/sales': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/customer-monthly-sales': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/sales-vs-consumable-cost': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/customer-count': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/equipment-count': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/usage': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/as-consumable-cost': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/as-type': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/report/engineer': { owner: 'full', manager: 'full', member: 'hidden' },

    // 매출증빙
    '/partner/sales-evidence/tax-invoice': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/sales-evidence/completed': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/payment/cash-receipt': { owner: 'full', manager: 'full', member: 'hidden' },

    // 원장
    '/partner/ledger/transaction': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/ledger/advance-payment': { owner: 'full', manager: 'full', member: 'hidden' },

    // 계좌/CMS
    '/partner/payment/account-lookup': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/payment/cms-member': { owner: 'full', manager: 'full', member: 'hidden' },
    '/partner/payment/cms-withdrawal': { owner: 'full', manager: 'full', member: 'hidden' },
};

/**
 * 특정 경로에 대한 권한 레벨을 반환합니다.
 * @param {string} path - 라우트 경로
 * @param {string} userType - 사용자 타입 (owner, manager, member)
 * @returns {'full' | 'readonly' | 'hidden'} 권한 레벨
 */
export function getPermissionLevel(path, userType) {
    const permission = permissions[path];
    if (!permission) {
        // 정의되지 않은 경로는 기본적으로 full 권한
        return 'full';
    }
    return permission[userType] || 'hidden';
}

/**
 * 메뉴에 접근 가능한지 확인합니다 (hidden이 아닌 경우).
 * @param {string} path - 라우트 경로
 * @param {string} userType - 사용자 타입
 * @returns {boolean} 접근 가능 여부
 */
export function canAccessMenu(path, userType) {
    return getPermissionLevel(path, userType) !== 'hidden';
}

/**
 * 메뉴 노출 여부를 확인합니다. 서버 config 기반 숨김(hiddenMenus)과 userType 권한을 함께 봅니다.
 * GNB와 페이지 내부 서브메뉴가 같은 규칙으로 판정하도록 노출 판정은 이 함수만 사용합니다.
 * @param {string} path - 라우트 경로
 * @param {Set<string>} hiddenMenuSet - hiddenMenus 스토어 값
 * @param {string} userType - 사용자 타입
 * @returns {boolean} 노출 여부
 */
export function isMenuVisible(path, hiddenMenuSet, userType) {
    return !hiddenMenuSet.has(path) && canAccessMenu(path, userType);
}

/**
 * 읽기 전용 권한인지 확인합니다.
 * @param {string} path - 라우트 경로
 * @param {string} userType - 사용자 타입
 * @returns {boolean} 읽기 전용 여부
 */
export function isReadonly(path, userType) {
    return getPermissionLevel(path, userType) === 'readonly';
}

/**
 * 전체 권한(CRUD)을 가지고 있는지 확인합니다.
 * @param {string} path - 라우트 경로
 * @param {string} userType - 사용자 타입
 * @returns {boolean} 전체 권한 여부
 */
export function hasFullAccess(path, userType) {
    return getPermissionLevel(path, userType) === 'full';
}

/**
 * 현재 세션의 사용자 타입을 가져옵니다.
 * @returns {string} 사용자 타입
 */
export function getCurrentUserType() {
    const user = JSON.parse(localStorage.getItem("user") || '{}');
    return user.Type || 'member';
}
